/**
 * Requêtes de collision contre le décor statique (rochers, couverts), via Rapier.
 * Les vaisseaux et projectiles ne sont pas dans Rapier : leurs tests sont analytiques (sphères),
 * ce qui évite de synchroniser des corps dynamiques à chaque tick.
 */
import RAPIER from '@dimforge/rapier3d-compat';
import type { ArenaData } from '../../data';
import { Vec3 } from '../core/math';
import type { GroundField } from './ground';

let initPromise: Promise<void> | null = null;

/** Charge le module WASM de Rapier une seule fois (navigateur et Node). */
export function initPhysics(): Promise<void> {
  initPromise ??= RAPIER.init();
  return initPromise;
}

export interface RayHit {
  distance: number;
  point: Vec3;
  normal: Vec3;
  obstacleId: string;
}

export interface SweepHit {
  distance: number;
  normal: Vec3;
}

export type ObstacleDef = ArenaData['obstacles'][number];

/** Points d'une icosphère subdivisée, pour approcher un ellipsoïde par une enveloppe convexe. */
function unitSpherePoints(subdivisions = 2): Vec3[] {
  const t = (1 + Math.sqrt(5)) / 2;
  const verts = [
    [-1, t, 0], [1, t, 0], [-1, -t, 0], [1, -t, 0],
    [0, -1, t], [0, 1, t], [0, -1, -t], [0, 1, -t],
    [t, 0, -1], [t, 0, 1], [-t, 0, -1], [-t, 0, 1],
  ].map((v) => Vec3.from(v).normalized());
  let faces = [
    [0, 11, 5], [0, 5, 1], [0, 1, 7], [0, 7, 10], [0, 10, 11],
    [1, 5, 9], [5, 11, 4], [11, 10, 2], [10, 7, 6], [7, 1, 8],
    [3, 9, 4], [3, 4, 2], [3, 2, 6], [3, 6, 8], [3, 8, 9],
    [4, 9, 5], [2, 4, 11], [6, 2, 10], [8, 6, 7], [9, 8, 1],
  ];
  for (let s = 0; s < subdivisions; s++) {
    const cache = new Map<string, number>();
    const mid = (a: number, b: number) => {
      const key = a < b ? `${a}_${b}` : `${b}_${a}`;
      const hit = cache.get(key);
      if (hit !== undefined) return hit;
      const p = verts[a]!.add(verts[b]!).normalized();
      verts.push(p);
      cache.set(key, verts.length - 1);
      return verts.length - 1;
    };
    const next: number[][] = [];
    for (const [a, b, c] of faces as [number, number, number][]) {
      const ab = mid(a, b), bc = mid(b, c), ca = mid(c, a);
      next.push([a, ab, ca], [b, bc, ab], [c, ca, bc], [ab, bc, ca]);
    }
    faces = next;
  }
  return verts;
}

const SPHERE_POINTS = unitSpherePoints(2);

/** Points de l'enveloppe d'un obstacle dans son repère local (ellipsoïde). Partagés avec le rendu. */
export function obstacleHullPoints(semiAxes: readonly number[]): Vec3[] {
  return SPHERE_POINTS.map((p) => new Vec3(p.x * semiAxes[0]!, p.y * semiAxes[1]!, p.z * semiAxes[2]!));
}

/**
 * Variante de rocher d'un obstacle : même formule pour la simulation et le rendu (rock_N.glb),
 * ou −1 sans enveloppe extraite (ellipsoïde).
 */
export function rockVariantIndex(obstacle: { variant: number }, variantCount: number): number {
  return variantCount > 0 ? obstacle.variant % variantCount : -1;
}

/** Enveloppe d'un obstacle : celle du modèle de rocher (cube unité étiré aux diamètres), sinon l'ellipsoïde. */
export function hullPointsFor(obstacle: ObstacleDef, rockHulls: readonly (readonly number[])[][]): Vec3[] {
  const index = rockVariantIndex(obstacle, rockHulls.length);
  if (index < 0) return obstacleHullPoints(obstacle.semiAxes);
  const [a, b, c] = obstacle.semiAxes;
  return rockHulls[index]!.map((p) => new Vec3(p[0]! * a! * 2, p[1]! * b! * 2, p[2]! * c! * 2));
}

export class CollisionWorld {
  private readonly world: RAPIER.World;
  private readonly idByHandle = new Map<number, string>();
  readonly obstacles: ObstacleDef[];

  constructor(
    obstacles: ObstacleDef[],
    rockHulls: readonly (readonly number[])[][] = [],
    boxes: readonly { id: string; center: Vec3; rotation: { x: number; y: number; z: number; w: number }; half: Vec3 }[] = [],
    /** Sol des zones de surface : champ de hauteurs (voir ground.ts). */
    ground: GroundField | null = null,
  ) {
    this.world = new RAPIER.World({ x: 0, y: 0, z: 0 });
    this.obstacles = obstacles;
    for (const obstacle of obstacles) {
      const points = hullPointsFor(obstacle, rockHulls);
      const buffer = new Float32Array(points.length * 3);
      points.forEach((p, i) => buffer.set([p.x, p.y, p.z], i * 3));
      const desc = RAPIER.ColliderDesc.convexHull(buffer);
      if (!desc) throw new Error(`Enveloppe convexe impossible pour ${obstacle.id}`);
      const [px, py, pz] = obstacle.position;
      const [qx, qy, qz, qw] = obstacle.rotation;
      desc.setTranslation(px, py, pz).setRotation({ x: qx, y: qy, z: qz, w: qw });
      const collider = this.world.createCollider(desc);
      this.idByHandle.set(collider.handle, obstacle.id);
    }
    for (const box of boxes) {
      const desc = RAPIER.ColliderDesc.cuboid(box.half.x, box.half.y, box.half.z)
        .setTranslation(box.center.x, box.center.y, box.center.z)
        .setRotation({ x: box.rotation.x, y: box.rotation.y, z: box.rotation.z, w: box.rotation.w });
      this.idByHandle.set(this.world.createCollider(desc).handle, box.id);
    }
    if (ground) {
      const desc = RAPIER.ColliderDesc.heightfield(ground.n - 1, ground.n - 1, ground.heights, { x: ground.size, y: 1, z: ground.size });
      this.idByHandle.set(this.world.createCollider(desc).handle, 'ground');
    }
    // Met à jour la structure d'accélération des requêtes.
    this.world.step();
  }

  free(): void {
    this.world.free();
  }

  raycast(origin: Vec3, direction: Vec3, maxDistance: number): RayHit | null {
    const dir = direction.normalized();
    if (dir.isNearlyZero() || maxDistance <= 0) return null;
    const ray = new RAPIER.Ray(origin, dir);
    const hit = this.world.castRayAndGetNormal(ray, maxDistance, true);
    if (!hit) return null;
    const distance = hit.timeOfImpact;
    return {
      distance,
      point: origin.add(dir.scale(distance)),
      normal: new Vec3(hit.normal.x, hit.normal.y, hit.normal.z),
      obstacleId: this.idByHandle.get(hit.collider.handle) ?? '',
    };
  }

  /** Balayage d'une sphère le long de `direction` sur `maxDistance`. */
  /**
   * `stopAtPenetration` faux : une sphère qui démarre en léger recouvrement peut s'en dégager
   * (sinon un vaisseau collé à une paroi restait bloqué indéfiniment).
   */
  sphereCast(origin: Vec3, direction: Vec3, radius: number, maxDistance: number, stopAtPenetration = true): SweepHit | null {
    const dir = direction.normalized();
    if (dir.isNearlyZero() || maxDistance <= 0) return null;
    const hit = this.world.castShape(origin, { x: 0, y: 0, z: 0, w: 1 }, dir, new RAPIER.Ball(radius), 0, maxDistance, stopAtPenetration);
    if (!hit) return null;
    return { distance: hit.time_of_impact, normal: new Vec3(hit.normal1.x, hit.normal1.y, hit.normal1.z).normalized() };
  }

  overlapsSphere(center: Vec3, radius: number): boolean {
    let found = false;
    this.world.intersectionsWithShape(center, { x: 0, y: 0, z: 0, w: 1 }, new RAPIER.Ball(radius), () => {
      found = true;
      return false;
    });
    return found;
  }

  /** Vrai si aucun obstacle ne coupe le segment a→b. */
  lineOfSight(a: Vec3, b: Vec3): boolean {
    const delta = b.sub(a);
    const distance = delta.length();
    if (distance < 1e-6) return true;
    return this.raycast(a, delta, distance) === null;
  }

  /**
   * Déplace une sphère en glissant sur les obstacles (jusqu'à 3 contacts par tick).
   * Retourne la nouvelle position et la vitesse privée de ses composantes entrant dans les obstacles.
   */
  slideMove(position: Vec3, velocity: Vec3, radius: number, dt: number): { position: Vec3; velocity: Vec3; blocked: Vec3 | null } {
    let pos = position.clone();
    let vel = velocity.clone();
    let remaining = vel.scale(dt);
    let blocked: Vec3 | null = null;
    const skin = 0.02;
    for (let i = 0; i < 3; i++) {
      const dist = remaining.length();
      if (dist < 1e-6) break;
      const hit = this.sphereCast(pos, remaining, radius, dist, false);
      if (!hit) {
        pos = pos.add(remaining);
        break;
      }
      const travel = Math.max(0, hit.distance - skin);
      const dir = remaining.scale(1 / dist);
      pos = pos.add(dir.scale(travel));
      const n = hit.normal;
      blocked = n;
      const rest = remaining.scale((dist - travel) / dist);
      remaining = rest.sub(n.scale(Math.min(0, rest.dot(n))));
      const vn = vel.dot(n);
      if (vn < 0) vel = vel.sub(n.scale(vn));
    }
    return { position: pos, velocity: vel, blocked };
  }
}
